import React from 'react';
import {Avatar, Box, CircularProgress, Link as MuiLink, Popover, Stack, Typography} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {Link as RouterLink} from 'react-router';
import {fetchWikidataEntity, searchWikidataEntities} from './prosemirror-editor/helpers/wikidataHelpers';
import {getEntityKBUrl, getWikidataUrl} from '../utils/entityLinks';
import {getEntityTypeLabel} from '../utils/entityTypeMeta';

// Entity details dialog that enriches local entity data with a Wikidata image + description.
export default function EntityDetailsDialog({open, anchorEl, entity, onClose}) {
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState(null);
    const [wikidataData, setWikidataData] = React.useState(null);

    React.useEffect(() => {
        let cancelled = false;
        if (!open || !entity?.canonicalName) {
            setWikidataData(null);
            setLoading(false);
            setError(null);
            return;
        }
        setLoading(true);
        setError(null);
        setWikidataData(null);

        (async () => {
            try {
                const results = await searchWikidataEntities(entity.canonicalName);
                if (cancelled) return;
                const firstId = results[0]?.id ?? null;
                if (firstId) {
                    const data = await fetchWikidataEntity(firstId);
                    if (!cancelled) setWikidataData(data);
                }
            } catch (e) {
                if (!cancelled) setError(e);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [open, entity?.canonicalName]);

    const title = wikidataData?.label || entity?.canonicalName || 'Entity';
    const description = wikidataData?.description || entity?.description || null;
    const imageUrl = wikidataData?.imageUrl || null;
    const wikidataId = wikidataData?.id || entity?.wikidataId || null;
    const kbUrl = wikidataId ? getEntityKBUrl(wikidataId) : null;
    const wikidataUrl = wikidataData?.id ? getWikidataUrl(wikidataData.id) : null;

    return (
        <Popover
            anchorEl={anchorEl}
            open={!!open}
            onClose={onClose}
            anchorOrigin={{vertical: 'top', horizontal: 'center'}}
            transformOrigin={{vertical: 'bottom', horizontal: 'center'}}
            slotProps={{paper: {sx: {p: 1.5, width: 360}}}}
        >
            <Stack spacing={1.5}>
                {loading ? (
                    <Box sx={{px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                        <CircularProgress size={20}/>
                    </Box>
                ) : error ? (
                    <Box sx={{px: 2, py: 1.5}}>
                        <Typography color="error" variant="body2">Failed to load entity data.</Typography>
                    </Box>
                ) : (
                    <>
                        <Stack direction="row" spacing={1.5} sx={{py: 0.5, px: 0.5}}>
                            <Stack spacing={0.5} sx={{flex: 1, minWidth: 0}}>
                                {kbUrl ? (
                                    <MuiLink
                                        component={RouterLink}
                                        to={kbUrl}
                                        variant="body1"
                                        fontWeight={600}
                                        sx={{alignSelf: 'flex-start'}}
                                        underline="hover"
                                        onClick={onClose}
                                    >
                                        {title}
                                    </MuiLink>
                                ) : (
                                    <Typography variant="body1" fontWeight={600}>{title}</Typography>
                                )}
                                {wikidataUrl && (
                                    <MuiLink
                                        href={wikidataUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        variant="caption"
                                        sx={{display: 'inline-flex', alignItems: 'center', gap: 0.5}}
                                    >
                                        View on Wikidata <OpenInNewIcon fontSize="inherit"/>
                                    </MuiLink>
                                )}
                                {description && (
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                            display: '-webkit-box',
                                            WebkitLineClamp: 4,
                                            WebkitBoxOrient: 'vertical',
                                            overflow: 'hidden',
                                        }}
                                    >
                                        {description}
                                    </Typography>
                                )}
                            </Stack>

                            <Box sx={{width: 64, minWidth: 64}}>
                                <Avatar variant="rounded" src={imageUrl || undefined} alt={title}
                                        sx={{width: 64, height: 64}}>
                                    {(title || '?')[0].toUpperCase()}
                                </Avatar>
                            </Box>
                        </Stack>

                        {(entity?.entityType || entity?.confidence != null) && (
                            <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap', px: 0.5}}>
                                {entity?.entityType && (
                                    <Typography variant="caption" sx={{
                                        bgcolor: 'primary.main', color: 'primary.contrastText',
                                        px: 1, py: 0.5, borderRadius: 1, fontWeight: 600,
                                    }}>
                                        {entity.entityType && getEntityTypeLabel(entity.entityType)}
                                    </Typography>
                                )}
                                {entity?.confidence != null && (
                                    <Typography variant="caption" sx={{
                                        bgcolor: 'grey.300', color: 'text.primary',
                                        px: 1, py: 0.5, borderRadius: 1,
                                    }}>
                                        {Math.round(entity.confidence * 100)}% confidence
                                    </Typography>
                                )}
                            </Box>
                        )}
                    </>
                )}
            </Stack>
        </Popover>
    );
}
